import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { docsApi } from '../api';
import { useSocket } from '../../../shared/socket/useSocket';

// 02.6-docs.md §4, paso "a" -- hook de datos desde cero (a diferencia de calendar, acá no
// existía ningún React Query previo: cada `docsApi.*` era un `await` suelto dentro de un
// `useState`+`useEffect` manual, sin cache ni invalidación). Sigue el mismo patrón
// invalidation-first que `useCalendarData.js`/`useDynamicTableData.js`.

export function useDocsList(spaceId) {
    return useQuery({
        queryKey: ['docs', 'list', spaceId],
        queryFn: async () => {
            const data = await docsApi.list({ spaceId });
            return data.success && Array.isArray(data.docs) ? data.docs : [];
        },
        enabled: !!spaceId,
        refetchOnWindowFocus: false,
    });
}

export function useDoc(docId) {
    return useQuery({
        queryKey: ['docs', 'doc', docId],
        queryFn: async () => {
            const data = await docsApi.getById(docId);
            return data.success ? data.doc : null;
        },
        enabled: !!docId,
        refetchOnWindowFocus: false,
    });
}

// Las mutaciones de escritura + el socket `doc_updated` invalidan cualquier lista cacheada
// (todos los `spaceId` vistos) y el doc individual -- mismo criterio que
// `useCalendarData.js`'s `invalidateEvents`.
function invalidateDocs(qc, docId) {
    // Devuelve la promesa (no fire-and-forget): `onSuccess` la propaga, así que
    // `mutateAsync(...)` de cualquier mutación de abajo no se resuelve hasta que el refetch
    // termina -- necesario para los call-sites que encadenan un `setEditingId`/similar justo
    // después de crear/actualizar un doc y esperan que el árbol ya refleje el cambio.
    const promises = [qc.invalidateQueries({ queryKey: ['docs', 'list'] })];
    if (docId) promises.push(qc.invalidateQueries({ queryKey: ['docs', 'doc', docId] }));
    return Promise.all(promises);
}

function useInvalidateDocs(docId) {
    const qc = useQueryClient();
    return () => invalidateDocs(qc, docId);
}

// 02.6-docs.md §0.5 -- `doc_updated` es un `io.emit` sin scope de sala (`docRouter.js:90`),
// mismo trade-off ya aceptado por calendar/databases para sus propios eventos sin scope:
// cualquier cliente conectado en cualquier espacio invalida ante cualquier doc actualizado en
// todo el sistema, no solo el propio. `ComponentDocs.jsx` ya tenía su propio
// `useSocket({doc_updated: () => fetchDocs()})` -- este hook lo reemplaza uno a uno.
export function useDocsSocketSync() {
    const qc = useQueryClient();
    useSocket(useMemo(() => ({
        doc_updated: (data) => invalidateDocs(qc, data?.id),
    }), [qc]));
}

export function useCreateDoc() {
    const invalidate = useInvalidateDocs();
    return useMutation({
        mutationFn: (payload) => docsApi.create(payload),
        onSuccess: invalidate,
    });
}

export function useUpdateDoc() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }) => docsApi.update(id, payload),
        onSuccess: (_data, { id }) => invalidateDocs(qc, id),
    });
}

export function useDeleteDoc() {
    const invalidate = useInvalidateDocs();
    return useMutation({
        mutationFn: (id) => docsApi.remove(id),
        onSuccess: invalidate,
    });
}

export function useHardDeleteDocs() {
    const invalidate = useInvalidateDocs();
    return useMutation({
        mutationFn: (ids) => docsApi.hardDelete({ ids }),
        onSuccess: invalidate,
    });
}

export function useToggleMediaVisibility() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, mediaKey, access }) => docsApi.toggleMediaVisibility(id, mediaKey, access),
        onSuccess: (_data, { id }) => invalidateDocs(qc, id),
    });
}

export function useToggleCoverVisibility() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, access }) => docsApi.toggleCoverVisibility(id, access),
        onSuccess: (_data, { id }) => invalidateDocs(qc, id),
    });
}
